/**
 * Job Files card (Issue #719) — every live file on THIS job, in one place.
 *
 * Before this card the tech screen fetched attachments five categories at a
 * time and never asked for `document`, so the job's own original work order —
 * which lands as a `document` with a provenance note — only existed inside the
 * Site History tab, buried among every other job's files for that site. Techs
 * arrived on site without the paper the job came from.
 *
 * Presentational: the query lives in job-detail.tsx (its photo list, wrap-up
 * count and report-copy gating all read the same data), so this component takes
 * files in and hands interactions back out. Derivation lives in job-files.ts.
 */
import { useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    Camera,
    Check,
    ChevronDown,
    Download,
    FileText,
    Image as ImageIcon,
    Loader2,
    MessageSquare,
    Paperclip,
    Plus,
    RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AttachmentImage } from "@/components/attachment-image";
import { cn } from "@/lib/utils";
import {
    CHIP_LABEL,
    chipCounts,
    formatFileSize,
    HERO_TITLE,
    KIND_LABEL,
    matchesChip,
    partitionHeroFiles,
    whoAndWhen,
    type FileChip,
    type JobFile,
} from "./job-files";
import { photoDisplayName } from "../redesign-copy";

/** Rows shown before "Show all" — enough to be useful, short enough to scan. */
const COLLAPSED_ROWS = 6;

export interface JobFilesCardProps {
    /** #948 J9 — readable photo names behind `field_redesign_v2`. */
    redesign?: boolean;
    files: JobFile[];
    serviceTitanFiles?: JobFile[];
    /** True only on a vendor WO job that has no WO paper attached at all. */
    originalWorkOrderMissing?: boolean;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    /** False when the device has no signal AND nothing is cached yet. */
    isOnline?: boolean;
    /** Attachment ids confirmed present in the offline cache. */
    offlineReadyIds?: Set<string>;
    /** Ids added since this tech last opened the card. */
    newFileIds?: Set<string>;
    onPreview: (file: JobFile) => void;
    onDownload: (file: JobFile) => void;
    onOpenPhoto: (file: JobFile) => void;
    /** Null hides the add affordance (wrong job status, or helper mode). */
    onAddFiles: ((files: File[]) => void) | null;
    isUploading?: boolean;
    /** Null hides the bulk download (offline, or nothing to zip). */
    onDownloadAll: (() => void) | null;
    isDownloadingAll?: boolean;
    onMessageOffice?: () => void;
    className?: string;
}

function SourceLine({ file, offlineReady }: { file: JobFile; offlineReady: boolean }) {
    return (
        <div className="flex items-center gap-1.5 text-xs text-foreground/70">
            <span className="truncate">{whoAndWhen(file)}</span>
            {file.fileSize > 0 && <span className="shrink-0">· {formatFileSize(file.fileSize)}</span>}
            {offlineReady && (
                <span className="flex shrink-0 items-center gap-0.5 text-green-700 dark:text-green-400">
                    <Check className="h-3 w-3" /> Saved offline
                </span>
            )}
        </div>
    );
}

/** The one or two work orders pinned above the fold — never collapsed. */
function HeroRow({
    file,
    accent,
    offlineReady,
    isNew,
    onPreview,
    onDownload,
    redesign,
}: {
    /** #948 J9 — a uuid is not a name. */
    redesign?: boolean;
    file: JobFile;
    accent: "primary" | "muted";
    offlineReady: boolean;
    isNew: boolean;
    onPreview: () => void;
    onDownload: () => void;
}) {
    return (
        <div
            className={cn(
                "flex items-center gap-3 rounded-lg border border-l-4 p-3",
                accent === "primary" ? "border-l-primary bg-primary/5" : "border-l-foreground/30",
            )}
            data-testid={`job-file-hero-${file.kind}`}
        >
            <FileText className="h-5 w-5 shrink-0 text-foreground/70" />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold">{HERO_TITLE[file.kind] ?? KIND_LABEL[file.kind]}</span>
                    {isNew && (
                        <Badge variant="secondary" className="h-4 px-1 text-[10px] font-bold">
                            NEW
                        </Badge>
                    )}
                </div>
                {/* #819 B8: the office's label beats the raw filename. */}
                <div
                    className="truncate text-sm"
                    title={
                        redesign
                            ? `${photoDisplayName(file)} — ${file.fileName}`
                            : file.photoTag
                              ? `${file.photoTag} — ${file.fileName}`
                              : file.fileName
                    }
                >
                    {redesign ? photoDisplayName(file) : (file.photoTag ?? file.fileName)}
                </div>
                <SourceLine file={file} offlineReady={offlineReady} />
            </div>
            <div className="flex shrink-0 items-center gap-1">
                <Button variant="outline" size="sm" className="h-10" onClick={onPreview}>
                    View
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 w-10 p-0"
                    onClick={onDownload}
                    aria-label={`Download ${file.fileName}`}
                >
                    <Download className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}

function FileRow({
    file,
    offlineReady,
    isNew,
    onOpen,
    onDownload,
    redesign,
    index,
}: {
    redesign?: boolean;
    index?: number;
    file: JobFile;
    offlineReady: boolean;
    isNew: boolean;
    onOpen: () => void;
    onDownload: () => void;
}) {
    return (
        <div className="flex min-h-[44px] items-center gap-2 px-3 py-2">
            <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                {file.isImage ? (
                    <span className="h-9 w-9 shrink-0 overflow-hidden rounded border bg-muted">
                        <AttachmentImage
                            attachmentId={file.id}
                            alt={file.photoTag ?? file.fileName}
                            variant="thumb"
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </span>
                ) : (
                    <FileText className="h-4 w-4 shrink-0 text-foreground/70" />
                )}
                <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                        {/* #819 B8: label over filename — a GUID-string photo
                            name tells a tech nothing; the office's label does. */}
                        <span
                            className="min-w-0 truncate text-sm"
                            title={
                                redesign
                                    ? `${photoDisplayName(file, { index })} — ${file.fileName}`
                                    : file.photoTag
                                      ? `${file.photoTag} — ${file.fileName}`
                                      : file.fileName
                            }
                        >
                            {redesign ? photoDisplayName(file, { index }) : (file.photoTag ?? file.fileName)}
                        </span>
                        {isNew && (
                            <Badge variant="secondary" className="h-4 shrink-0 px-1 text-[10px] font-bold">
                                NEW
                            </Badge>
                        )}
                    </span>
                    <SourceLine file={file} offlineReady={offlineReady} />
                </span>
            </button>
            <Badge variant="outline" className="shrink-0 text-[10px] text-foreground/70">
                {KIND_LABEL[file.kind]}
            </Badge>
            <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 shrink-0 p-0"
                onClick={onDownload}
                aria-label={`Download ${file.fileName}`}
            >
                <Download className="h-4 w-4" />
            </Button>
        </div>
    );
}

export function JobFilesCard({
    redesign,
    files,
    serviceTitanFiles = [],
    originalWorkOrderMissing = false,
    isLoading = false,
    isError = false,
    onRetry,
    isOnline = true,
    offlineReadyIds,
    newFileIds,
    onPreview,
    onDownload,
    onOpenPhoto,
    onAddFiles,
    isUploading = false,
    onDownloadAll,
    isDownloadingAll = false,
    onMessageOffice,
    className,
}: JobFilesCardProps) {
    const [expanded, setExpanded] = useState(false);
    const [showAllRows, setShowAllRows] = useState(false);
    const [showServiceTitan, setShowServiceTitan] = useState(false);
    const [chip, setChip] = useState<FileChip>("all");
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const { original, completed, rest } = useMemo(() => partitionHeroFiles(files), [files]);
    const counts = useMemo(() => chipCounts(rest), [rest]);
    const visible = useMemo(() => rest.filter((f) => matchesChip(f, chip)), [rest, chip]);
    const rows = showAllRows ? visible : visible.slice(0, COLLAPSED_ROWS);
    const newCount = newFileIds?.size ?? 0;

    const isOfflineReady = (f: JobFile) => offlineReadyIds?.has(f.id) ?? false;
    const isNew = (f: JobFile) => newFileIds?.has(f.id) ?? false;
    const openFile = (f: JobFile) => (f.isImage ? onOpenPhoto(f) : onPreview(f));

    const header = (
        <div className="flex min-h-[44px] flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
                <Paperclip className="h-4 w-4 text-foreground/70" />
                <span className="text-sm font-semibold">Job Files</span>
                {files.length > 0 && (
                    <Badge variant="secondary" className="text-[10px]">
                        {files.length}
                    </Badge>
                )}
                {newCount > 0 && (
                    <Badge className="bg-primary text-[10px] text-primary-foreground">{newCount} new</Badge>
                )}
            </div>
            <div className="flex items-center gap-1.5">
                {onDownloadAll && files.length > 1 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-10"
                        disabled={isDownloadingAll}
                        onClick={onDownloadAll}
                    >
                        {isDownloadingAll ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                            <Download className="mr-1.5 h-4 w-4" />
                        )}
                        All
                    </Button>
                )}
                {onAddFiles && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*,application/pdf"
                            className="hidden"
                            data-testid="job-files-input"
                            onChange={(e) => {
                                const picked = Array.from(e.target.files ?? []);
                                if (picked.length) onAddFiles(picked);
                                e.target.value = "";
                            }}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-10"
                            disabled={isUploading}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {isUploading ? (
                                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                                <Plus className="mr-1.5 h-4 w-4" />
                            )}
                            Add file
                        </Button>
                    </>
                )}
            </div>
        </div>
    );

    let body: React.ReactNode;
    if (isLoading) {
        body = (
            <div className="space-y-2" data-testid="job-files-loading">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="h-11 animate-pulse rounded-lg bg-muted" />
                ))}
            </div>
        );
    } else if (isError) {
        // Never a blank card: an empty-looking card reads as "there are no
        // files", which is the exact confusion this card exists to end.
        body = (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                <span className="min-w-0 flex-1 text-sm">Couldn&apos;t load this job&apos;s files.</span>
                {onRetry && (
                    <Button variant="outline" size="sm" className="h-10 shrink-0" onClick={onRetry}>
                        <RefreshCw className="mr-1.5 h-4 w-4" /> Retry
                    </Button>
                )}
            </div>
        );
    } else if (files.length === 0 && serviceTitanFiles.length === 0) {
        body = (
            <div className="space-y-2">
                {originalWorkOrderMissing ? (
                    <MissingWorkOrderRow onMessageOffice={onMessageOffice} />
                ) : (
                    <p className="text-sm text-foreground/70">
                        {isOnline ? "No files on this job yet." : "Files will load when you're back on signal."}
                    </p>
                )}
            </div>
        );
    } else {
        body = (
            <div className="space-y-2">
                {originalWorkOrderMissing && !original && <MissingWorkOrderRow onMessageOffice={onMessageOffice} />}
                {original && (
                    <HeroRow
                        redesign={redesign}
                        file={original}
                        accent="primary"
                        offlineReady={isOfflineReady(original)}
                        isNew={isNew(original)}
                        onPreview={() => onPreview(original)}
                        onDownload={() => onDownload(original)}
                    />
                )}
                {completed && (
                    <HeroRow
                        redesign={redesign}
                        file={completed}
                        accent="muted"
                        offlineReady={isOfflineReady(completed)}
                        isNew={isNew(completed)}
                        onPreview={() => onPreview(completed)}
                        onDownload={() => onDownload(completed)}
                    />
                )}

                {rest.length > 0 && !expanded && (
                    <Button
                        variant="ghost"
                        className="h-11 w-full justify-between"
                        onClick={() => setExpanded(true)}
                        data-testid="job-files-expand"
                    >
                        <span className="flex items-center gap-2 text-sm">
                            {counts.photos > 0 ? (
                                <ImageIcon className="h-4 w-4 text-foreground/70" />
                            ) : (
                                <FileText className="h-4 w-4 text-foreground/70" />
                            )}
                            Show {rest.length} more {rest.length === 1 ? "file" : "files"}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                )}

                {rest.length > 0 && expanded && (
                    <>
                        {rest.length > 4 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {(Object.keys(CHIP_LABEL) as FileChip[]).map(
                                    (c) =>
                                        (c === "all" || counts[c] > 0) && (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => {
                                                    setChip(c);
                                                    setShowAllRows(false);
                                                }}
                                                className={cn(
                                                    "rounded-full border px-2.5 py-1 text-xs transition-colors",
                                                    chip === c
                                                        ? "border-primary bg-primary/10 font-medium text-primary"
                                                        : "text-foreground/70 hover:bg-muted/50",
                                                )}
                                            >
                                                {CHIP_LABEL[c]} ({counts[c]})
                                            </button>
                                        ),
                                )}
                            </div>
                        )}
                        {visible.length === 0 ? (
                            <p className="px-1 text-xs text-foreground/60">
                                No {CHIP_LABEL[chip].toLowerCase()} on this job.
                            </p>
                        ) : (
                            <div className="divide-y rounded-lg border">
                                {rows.map((f) => (
                                    <FileRow
                                        redesign={redesign}
                                        key={f.id}
                                        file={f}
                                        offlineReady={isOfflineReady(f)}
                                        isNew={isNew(f)}
                                        onOpen={() => openFile(f)}
                                        onDownload={() => onDownload(f)}
                                    />
                                ))}
                            </div>
                        )}
                        {visible.length > rows.length && (
                            <Button
                                variant="ghost"
                                className="h-11 w-full"
                                onClick={() => setShowAllRows(true)}
                                data-testid="job-files-show-all"
                            >
                                Show all {visible.length}
                            </Button>
                        )}
                    </>
                )}

                {serviceTitanFiles.length > 0 && (
                    <div className="rounded-lg border border-dashed">
                        <button
                            type="button"
                            className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-xs text-foreground/70"
                            onClick={() => setShowServiceTitan((v) => !v)}
                        >
                            <span className="min-w-0 flex-1">
                                {serviceTitanFiles.length} imported file{serviceTitanFiles.length === 1 ? "" : "s"}
                            </span>
                            <ChevronDown
                                className={cn("h-4 w-4 transition-transform", showServiceTitan && "rotate-180")}
                            />
                        </button>
                        {showServiceTitan && (
                            <div className="divide-y border-t">
                                {serviceTitanFiles.map((f) => (
                                    <FileRow
                                        redesign={redesign}
                                        key={f.id}
                                        file={f}
                                        offlineReady={isOfflineReady(f)}
                                        isNew={false}
                                        onOpen={() => openFile(f)}
                                        onDownload={() => onDownload(f)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {onAddFiles && files.length > 0 && (
                    <p className="flex items-center gap-1.5 px-1 text-xs text-foreground/60">
                        <Camera className="h-3.5 w-3.5" /> Photos and paperwork you add land here too.
                    </p>
                )}
            </div>
        );
    }

    return (
        <Card className={cn("bg-card", className)} id="job-files-card">
            <CardContent className="space-y-3 p-4">
                {header}
                {body}
            </CardContent>
        </Card>
    );
}

/**
 * A vendor work order with no WO paper attached is a real problem — the tech
 * cannot do the job right without it. It must read differently from "no files
 * yet", and it must offer a way out rather than just stating the fact.
 */
function MissingWorkOrderRow({ onMessageOffice }: { onMessageOffice?: () => void }) {
    return (
        <div className="flex items-center gap-2 rounded-lg border border-amber-400/60 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-950/30">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="min-w-0 flex-1 text-sm">No original work order is attached to this job.</span>
            {onMessageOffice && (
                <Button variant="outline" size="sm" className="h-10 shrink-0" onClick={onMessageOffice}>
                    <MessageSquare className="mr-1.5 h-4 w-4" /> Ask office
                </Button>
            )}
        </div>
    );
}
